import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  ArrowLeft, Search, ChevronLeft, ChevronRight, Package,
  Check, X, SlidersHorizontal, ArrowUpDown, Bookmark, Trash2, Tag, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_FILTERS = {
  search: '',
  availability: '',
  is_active: '',
  brand: '',
  product_type: '',
  label_field: '',
  label_value: '',
  price_min: '',
  price_max: '',
  has_overlay: '',
  missing: '',
};

const FILTER_LABELS = {
  search: 'Hledání',
  availability: 'Dostupnost',
  is_active: 'Status',
  brand: 'Značka',
  product_type: 'Typ',
  label_field: 'Label',
  price_min: 'Cena od',
  price_max: 'Cena do',
  has_overlay: 'Overlay',
  missing: 'Chybí',
};

const VALUE_LABELS = {
  'in stock': 'skladem',
  'out of stock': 'není skladem',
  preorder: 'předobjednávka',
  true: 'ano',
  false: 'ne',
  image_link: 'obrázek',
  price: 'cena',
  brand: 'značka',
  gtin: 'GTIN',
  description: 'popis',
  google_product_category: 'kategorie Google',
  __empty__: 'prázdný',
};

const selectClass = 'flex h-9 w-full rounded-md border border-input bg-white px-2 text-sm';
const inlineSelectClass = 'flex h-9 rounded-md border border-input bg-white px-2 text-sm';

// Jen vyplněné hodnoty — prázdné filtry do dotazu nepatří
function activeFilters(filters) {
  const out = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) out[key] = value;
  });
  if (!out.label_field) delete out.label_value;
  return out;
}

export default function ProductsPage() {
  const { projectId } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [showMore, setShowMore] = useState(false);
  const [options, setOptions] = useState(null);
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [sortBy, setSortBy] = useState('title_original');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [selected, setSelected] = useState([]);
  const [allMatching, setAllMatching] = useState(false);
  const [labelForm, setLabelForm] = useState({ field: 'custom_label_0', value: '' });
  const [showLabelForm, setShowLabelForm] = useState(false);
  const limit = 50;

  // Textová pole se promítnou do dotazu se zpožděním, ať se nedotazuje na každé písmeno
  useEffect(() => {
    const timer = setTimeout(() => setAppliedFilters(filters), 350);
    return () => clearTimeout(timer);
  }, [filters]);

  const query = useMemo(() => activeFilters(appliedFilters), [appliedFilters]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
        ...query,
      });
      const res = await api.get(`/projects/${projectId}/products?${params}`);
      setProducts(res.data.products);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      toast.error('Nepodařilo se načíst produkty');
    } finally {
      setLoading(false);
    }
  }, [projectId, page, sortBy, sortOrder, query]);

  const fetchSavedFilters = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/saved-filters`);
      setSavedFilters(res.data.filters || []);
    } catch (err) {
      // uložené filtry nejsou kritické
    }
  }, [projectId]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    fetchSavedFilters();
    api.get(`/projects/${projectId}/products/filter-options`)
      .then(res => setOptions(res.data))
      .catch(() => {});
  }, [projectId, fetchSavedFilters]);

  // Změna filtru ruší výběr i stránkování
  useEffect(() => {
    setPage(1);
    setSelected([]);
    setAllMatching(false);
  }, [query]);

  const setFilter = (key, value) => setFilters(f => ({ ...f, [key]: value }));
  const clearFilter = (key) => setFilters(f => ({
    ...f, [key]: '', ...(key === 'label_field' ? { label_value: '' } : {}),
  }));
  const clearAll = () => setFilters(EMPTY_FILTERS);

  const pageSelected = products.length > 0 && products.every(p => selected.includes(p.id));
  const selectedCount = allMatching ? total : selected.length;

  const scope = () => (allMatching ? { select_all: true, filters: query } : { product_ids: selected });

  const handleSelectPage = (checked) => {
    setAllMatching(false);
    setSelected(checked ? products.map(p => p.id) : []);
  };

  const handleSelect = (id, checked) => {
    setAllMatching(false);
    setSelected(checked ? [...selected, id] : selected.filter(s => s !== id));
  };

  const clearSelection = () => {
    setSelected([]);
    setAllMatching(false);
  };

  const handleBulkActive = async (active) => {
    try {
      const res = await api.post(`/projects/${projectId}/products/bulk-active`, { ...scope(), is_active: active });
      toast.success(`${res.data.count} produktů ${active ? 'aktivováno' : 'deaktivováno'}`);
      clearSelection();
      fetchProducts();
    } catch (err) {
      toast.error('Operace selhala');
    }
  };

  const handleBulkLabel = async () => {
    try {
      const res = await api.post(`/projects/${projectId}/products/bulk-label`, {
        ...scope(), [labelForm.field]: labelForm.value,
      });
      toast.success(`Label nastaven u ${res.data.count} produktů`);
      setShowLabelForm(false);
      clearSelection();
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nastavení labelu selhalo');
    }
  };

  const handleSaveFilter = async () => {
    if (!saveName.trim()) return;
    try {
      await api.post(`/projects/${projectId}/saved-filters`, { name: saveName.trim(), filters: query });
      toast.success('Filtr uložen');
      setSaveName('');
      fetchSavedFilters();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Uložení filtru selhalo');
    }
  };

  const handleDeleteSaved = async (id) => {
    try {
      await api.delete(`/projects/${projectId}/saved-filters/${id}`);
      fetchSavedFilters();
    } catch (err) {
      toast.error('Smazání filtru selhalo');
    }
  };

  const applySaved = (saved) => {
    setFilters({ ...EMPTY_FILTERS, ...Object.fromEntries(
      Object.entries(saved.filters || {}).map(([k, v]) => [k, String(v)])
    ) });
    setShowMore(true);
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const chips = Object.entries(query).filter(([key]) => key !== 'label_value');
  const chipValue = (key, value) => {
    if (key === 'label_field') {
      const v = query.label_value;
      return `${value.replace('custom_label_', 'L')} = ${v ? (VALUE_LABELS[v] || v) : 'vyplněný'}`;
    }
    return VALUE_LABELS[value] || value;
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center gap-4 mb-6">
        <Button asChild variant="ghost" size="icon" data-testid="back-btn">
          <Link to={`/projects/${projectId}`}>
            <ArrowLeft className="w-5 h-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-bold text-slate-900">Přehled produktů</h1>
          <p className="text-sm text-slate-500">
            {chips.length > 0 ? `${total} produktů odpovídá filtru` : `${total} produktů celkem`}
          </p>
        </div>
      </div>

      {/* Základní filtry */}
      <div className="flex flex-wrap gap-3 mb-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Hledat podle názvu, ID nebo značky..."
            value={filters.search}
            onChange={(e) => setFilter('search', e.target.value)}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <select
          value={filters.availability}
          onChange={e => setFilter('availability', e.target.value)}
          className={`${inlineSelectClass} w-[170px]`}
          data-testid="availability-filter"
        >
          <option value="">Dostupnost: vše</option>
          <option value="in stock">Skladem</option>
          <option value="out of stock">Není skladem</option>
          <option value="preorder">Předobjednávka</option>
        </select>
        <select
          value={filters.is_active}
          onChange={e => setFilter('is_active', e.target.value)}
          className={`${inlineSelectClass} w-[150px]`}
          data-testid="active-filter"
        >
          <option value="">Status: vše</option>
          <option value="true">Aktivní</option>
          <option value="false">Neaktivní</option>
        </select>
        <Button variant="outline" onClick={() => setShowMore(!showMore)} data-testid="more-filters-btn">
          <SlidersHorizontal className="w-4 h-4 mr-2" />
          Další filtry
        </Button>
      </div>

      {/* Rozšířené filtry */}
      {showMore && (
        <div className="mb-4 p-4 border border-slate-200 rounded-lg bg-slate-50 space-y-4" data-testid="more-filters">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <div className="text-xs text-slate-500">Značka</div>
              <select value={filters.brand} onChange={e => setFilter('brand', e.target.value)} className={selectClass}>
                <option value="">vše</option>
                {options?.brands.map(b => <option key={b.value} value={b.value}>{b.value} ({b.count})</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-slate-500">Typ produktu</div>
              <select value={filters.product_type} onChange={e => setFilter('product_type', e.target.value)} className={selectClass}>
                <option value="">vše</option>
                {options?.product_types.map(t => (
                  <option key={t.value} value={t.value}>{t.value.length > 60 ? `…${t.value.slice(-58)}` : t.value} ({t.count})</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-slate-500">Cena od – do (podle akční ceny)</div>
              <div className="flex gap-2">
                <Input type="number" min="0" placeholder={options?.price_min ?? 'od'} value={filters.price_min}
                  onChange={e => setFilter('price_min', e.target.value)} className="h-9" data-testid="price-min" />
                <Input type="number" min="0" placeholder={options?.price_max ?? 'do'} value={filters.price_max}
                  onChange={e => setFilter('price_max', e.target.value)} className="h-9" data-testid="price-max" />
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-slate-500">Custom label</div>
              <div className="flex gap-2">
                <select value={filters.label_field} onChange={e => setFilters(f => ({ ...f, label_field: e.target.value, label_value: '' }))} className={selectClass}>
                  <option value="">—</option>
                  {[0, 1, 2, 3, 4].map(i => <option key={i} value={`custom_label_${i}`}>label {i}</option>)}
                </select>
                <select value={filters.label_value} onChange={e => setFilter('label_value', e.target.value)}
                  className={selectClass} disabled={!filters.label_field}>
                  <option value="">vyplněný</option>
                  <option value="__empty__">prázdný</option>
                  {(options?.labels?.[filters.label_field] || []).map(v => (
                    <option key={v.value} value={v.value}>{v.value} ({v.count})</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-slate-500">Overlay na fotce</div>
              <select value={filters.has_overlay} onChange={e => setFilter('has_overlay', e.target.value)} className={selectClass}>
                <option value="">vše</option>
                <option value="true">má overlay</option>
                <option value="false">bez overlaye</option>
              </select>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-slate-500">Chybějící údaj</div>
              <select value={filters.missing} onChange={e => setFilter('missing', e.target.value)} className={selectClass}>
                <option value="">—</option>
                {['image_link', 'price', 'brand', 'gtin', 'description', 'google_product_category'].map(f => (
                  <option key={f} value={f}>chybí {VALUE_LABELS[f]}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Uložené filtry */}
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-200">
            <Bookmark className="w-4 h-4 text-slate-400" />
            {savedFilters.length === 0 && <span className="text-sm text-slate-500">Žádné uložené filtry</span>}
            {savedFilters.map(sf => (
              <span key={sf.id} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white pl-2 text-sm">
                <button onClick={() => applySaved(sf)} className="hover:text-amber-700" data-testid={`saved-filter-${sf.id}`}>
                  {sf.name}
                </button>
                <button onClick={() => handleDeleteSaved(sf.id)} className="px-1.5 py-1 text-slate-400 hover:text-red-600" title="Smazat">
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
            {chips.length > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <Input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="Název filtru"
                  className="h-8 w-44 text-sm" data-testid="save-filter-name" />
                <Button size="sm" variant="outline" onClick={handleSaveFilter} disabled={!saveName.trim()} data-testid="save-filter-btn">
                  Uložit filtr
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Aktivní filtry */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="active-filters">
          {chips.map(([key, value]) => (
            <span key={key} className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 px-3 py-1 text-xs">
              {FILTER_LABELS[key]}: {chipValue(key, value)}
              <button onClick={() => clearFilter(key)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
            </span>
          ))}
          <button onClick={clearAll} className="text-xs text-slate-500 hover:text-slate-900 underline">Zrušit filtry</button>
        </div>
      )}

      {/* Výběr a hromadné akce */}
      {selectedCount > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2" data-testid="bulk-bar">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-amber-900">
              Vybráno: {selectedCount} {allMatching ? '(vše, co odpovídá filtru)' : 'produktů'}
            </span>
            {!allMatching && pageSelected && total > products.length && (
              <button onClick={() => setAllMatching(true)} className="text-sm text-amber-700 underline" data-testid="select-all-matching">
                Vybrat všech {total}{chips.length > 0 ? ' odpovídajících filtru' : ''}
              </button>
            )}
            <button onClick={clearSelection} className="text-xs text-slate-500 underline">zrušit výběr</button>
            <div className="flex flex-wrap gap-2 ml-auto">
              <Button size="sm" variant="outline" onClick={() => handleBulkActive(true)} data-testid="bulk-activate-btn">
                <Check className="w-4 h-4 mr-1" /> Vrátit do exportu
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkActive(false)} data-testid="bulk-deactivate-btn">
                <X className="w-4 h-4 mr-1" /> Vyřadit z exportu
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowLabelForm(!showLabelForm)} data-testid="bulk-label-toggle">
                <Tag className="w-4 h-4 mr-1" /> Nastavit label
              </Button>
            </div>
          </div>
          {showLabelForm && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-amber-200">
              <select value={labelForm.field} onChange={e => setLabelForm({ ...labelForm, field: e.target.value })}
                className="flex h-8 rounded-md border border-input bg-white px-2 text-sm">
                {[0, 1, 2, 3, 4].map(i => <option key={i} value={`custom_label_${i}`}>custom_label_{i}</option>)}
              </select>
              <Input value={labelForm.value} onChange={e => setLabelForm({ ...labelForm, value: e.target.value })}
                placeholder="hodnota (prázdné = smazat)" className="h-8 w-56 text-sm" data-testid="bulk-label-value" />
              <Button size="sm" onClick={handleBulkLabel} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="bulk-label-apply">
                Použít na {selectedCount} produktů
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Tabulka */}
      {loading && products.length === 0 ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Package className="w-12 h-12 text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">Žádné produkty</h3>
          <p className="text-slate-500">{chips.length > 0 ? 'Filtru nic neodpovídá' : 'Importujte produkty z XML feedu'}</p>
        </div>
      ) : (
        <div className={`border border-slate-200 rounded-lg overflow-hidden ${loading ? 'opacity-60' : ''}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="w-12 py-3 px-4">
                    <Checkbox checked={pageSelected} onCheckedChange={handleSelectPage} data-testid="select-all-checkbox" />
                  </th>
                  <th className="py-3 px-2 w-14"></th>
                  <th className="py-3 px-4 text-left">
                    <button className="flex items-center gap-1 font-medium text-slate-600 hover:text-slate-900" onClick={() => toggleSort('original_id')}>
                      ID <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="py-3 px-4 text-left min-w-[220px]">
                    <button className="flex items-center gap-1 font-medium text-slate-600 hover:text-slate-900" onClick={() => toggleSort('title_original')}>
                      Název <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="py-3 px-4 text-left">
                    <button className="flex items-center gap-1 font-medium text-slate-600 hover:text-slate-900" onClick={() => toggleSort('price')}>
                      Cena <ArrowUpDown className="w-3 h-3" />
                    </button>
                  </th>
                  <th className="py-3 px-4 text-left">Dostupnost</th>
                  <th className="py-3 px-4 text-left">Custom labely</th>
                  <th className="py-3 px-4 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const image = product.overlay_image_link || product.selected_image_link || product.image_link;
                  return (
                    <tr key={product.id}
                      className="border-t border-slate-100 hover:bg-amber-50/30"
                      data-testid={`product-row-${product.id}`}>
                      <td className="py-3 px-4">
                        <Checkbox checked={allMatching || selected.includes(product.id)}
                          onCheckedChange={(checked) => handleSelect(product.id, checked)} data-testid={`select-${product.id}`} />
                      </td>
                      <td className="py-2 px-2">
                        {image ? (
                          <div className="relative w-10 h-10">
                            <img src={image} alt="" loading="lazy" className="w-10 h-10 object-cover rounded border border-slate-200" />
                            {product.overlay_image_link && (
                              <Sparkles className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 text-amber-500 bg-white rounded-full" />
                            )}
                          </div>
                        ) : <div className="w-10 h-10 rounded bg-slate-100" />}
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono text-xs text-slate-600">{product.original_id}</span>
                        {product.item_group_id && <div className="text-xs text-slate-400 mt-0.5">Group: {product.item_group_id}</div>}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium line-clamp-1 text-slate-900">
                          {product.title_modified || product.title_original}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                          {(product.size || product.color) && (
                            <span className="text-xs text-slate-400">{[product.size, product.color].filter(Boolean).join(' · ')}</span>
                          )}
                          {product.title_modified && (
                            <span className="text-xs text-slate-400 line-clamp-1">Původní: {product.title_original}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        {product.sale_price ? (
                          <>
                            <div className="font-medium text-rose-700">{product.sale_price}</div>
                            <div className="text-xs text-slate-400 line-through">{product.price}</div>
                          </>
                        ) : <span className="font-medium">{product.price || '-'}</span>}
                      </td>
                      <td className="py-3 px-4">
                        {product.availability === 'in stock' ? (
                          <Badge className="bg-green-100 text-green-800 border-green-200">Skladem</Badge>
                        ) : product.availability === 'out of stock' ? (
                          <Badge className="bg-red-100 text-red-800 border-red-200">Není skladem</Badge>
                        ) : product.availability ? (
                          <Badge className="bg-slate-100 text-slate-800 border-slate-200">{product.availability}</Badge>
                        ) : <span className="text-slate-400">-</span>}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {[0, 1, 2, 3, 4].map(i => {
                            const label = product[`custom_label_${i}`];
                            return label ? <Badge key={i} variant="outline" className="text-xs">L{i}: {label}</Badge> : null;
                          })}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {product.is_active
                          ? <Check className="w-5 h-5 text-green-600 mx-auto" />
                          : <X className="w-5 h-5 text-red-400 mx-auto" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-slate-500">
            Zobrazeno {(page - 1) * limit + 1} - {Math.min(page * limit, total)} z {total}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="prev-page-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm px-3">{page} / {pages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} data-testid="next-page-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
